/**
 * GlobalLowBalanceBanner
 *
 * App-wide wallet notices with a one-tap "Top up" CTA that opens the in-place
 * TopUpModal (no navigation).
 *
 * Threshold model (2026-08-14, aligned with the server's hard balance stop):
 *   - LIGHT (info, dismissible until 5 AM next morning): the balance is above
 *     empty but at or under the user's auto top-up threshold
 *     (users/{uid}/wallet/autoTopUp.thresholdUsd, default $25). Trial/comped
 *     accounts with an entitlement bypass are never nagged here.
 *   - CRITICAL (not dismissible): the balance is at or below $0 — the server
 *     balance stop has paused work. This state IGNORES the entitlement
 *     bypass: the platform rule pauses those accounts too.
 *
 * Both states stand down entirely when the account's balance stop is DISARMED
 * (accounts/{id}/config/spendLimits.balanceStop.enabled === false) — nothing has
 * paused, so there is nothing to report. See the `balanceStopArmed` note below.
 *
 * Permission gate: the banner is billing chrome — it renders only for viewers
 * who can act on or at least see the wallet (canManageBilling || canSeeWallet;
 * owners hold both).
 *
 * Rendering: registers with the surrounding GlobalBannerCenter (the ONE
 * global top strip; rotates when several notices are eligible) and renders
 * nothing itself. Standalone (no BannerBusContext above), it falls back to
 * rendering its legacy strip directly.
 *
 * Surface: signed-in viewers only, on every route except the mid-auth-flow
 * screens (common/bannerSurface.ts). The band does not hide itself merely
 * because the chat panel is open — with ONE exception since 2026-09-13: on a
 * narrow viewport where the chat IS the full screen, it stands down and the
 * chat's own ChatBalanceNotice carries the message. Same sentence twice on a
 * phone, with this band pinned above the takeover, was the duplication that
 * exception removes. See the `show` computation.
 *
 * Avatar: on the false→true transition of either state we emit a
 * `wallet.balance.low` bridge event once (not per render) so the avatar can
 * narrate the low balance.
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { isAskOwnerAudience } from './lowBalanceAudience';
import { useChatPanelFullScreen } from './chatPanelStore';
import { NARROW_VIEWPORT_QUERY } from '../../styles/breakpoints';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useLowBalanceWarning } from '../GlobalChatSystem/useLowBalanceWarning';
import { useMaybeWalletSummary } from '../../context/WalletSummaryContext';
import { useMaybeTopUp } from '../../context/TopUpModalContext';
import { useAutoTopUp } from '../../hooks/useAutoTopUp';
import { useUsageVisibility } from '../../hooks/useUsageVisibility';
import { broadcastBridgeEvent } from '../../services/agenticWorkflows/broadcastBridgeEvent';
import { usdToWu, formatWu, WU_LABEL, WU_FULL_NAME } from '../../config/workUnits';
import { BannerBusContext, useRegisterBanner } from '../common/GlobalBannerCenter';
import { useBannerSurface } from '../common/bannerSurface';
import {
  isDismissalActive,
  readDailyDismissal,
  writeDailyDismissal,
} from '../common/bannerDismissal';

/** Daily-5AM dismissal (light state only). v2: replaced the session-level key. */
const DISMISS_KEY = 'prest0n_lowbal_dismissed_v2';

/**
 * Surface gating moved to common/bannerSurface.ts (2026-08-31 consistency
 * directive). The rule this file used to own — "a '$0.00 critically low'
 * strip must never reach a signed-out visitor on a marketing page" — is
 * UNCHANGED and still enforced; it is simply asked on the axis that carries
 * it (is the viewer signed in?) instead of a 14-prefix route allowlist that
 * happened to exclude '/'. See that module's header for the full rationale.
 */

/**
 * In normal document flow (rendered directly below the app Header in
 * App.tsx) — the strip occupies its own band and pushes page content down
 * instead of overlaying the header (owner report 2026-07-31).
 */
/* R18 (owner directive 2026-08-09): the warning tone rides the Prest0 gold
   palette (PRESTO_PALETTE in common/PortalBanner.tsx) — the old #fde68a amber
   was off-palette. Critical KEEPS red: the owner explicitly reserves red/green
   for spend-severity distinctions. */
const Strip = styled.div<{ $critical?: boolean }>`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => (p.$critical ? '#7f1d1d' : '#7A5C00')};
  background: ${(p) => (p.$critical ? '#FDECEC' : '#FFF8E6')};
  border-bottom: 1px solid ${(p) => (p.$critical ? '#F2B8B5' : 'var(--brand-gold)')};
`;

const Message = styled.span`
  max-width: 80vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TopUpButton = styled.button<{ $critical?: boolean }>`
  flex: none;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: ${(p) => (p.$critical ? '#dc2626' : 'var(--brand-gold-hover)')};
  border: none;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(1.08); }
`;

const DismissButton = styled.button`
  flex: none;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  opacity: 0.7;
  padding: 2px 4px;
  line-height: 0;
  &:hover { opacity: 1; }
  svg { width: 16px; height: 16px; }
`;

export const GlobalLowBalanceBanner: React.FC = () => {
  const bus = useContext(BannerBusContext);
  const lb = useLowBalanceWarning();
  const walletSummary = useMaybeWalletSummary();
  const vis = useUsageVisibility();
  const chatFullScreen = useChatPanelFullScreen();
  // The same query string the stylesheets interpolate — a second copy of the
  // number is how the composer lost 100px to a 720-vs-820 disagreement.
  const [narrowViewport, setNarrowViewport] = useState<boolean>(() => {
    try { return window.matchMedia(NARROW_VIEWPORT_QUERY).matches; } catch { return false; }
  });
  useEffect(() => {
    let mq: MediaQueryList;
    try { mq = window.matchMedia(NARROW_VIEWPORT_QUERY); } catch { return undefined; }
    const onChange = () => setNarrowViewport(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const topUp = useMaybeTopUp();
  const navigate = useNavigate();
  const onAppSurface = useBannerSurface();

  // Firebase auth uid — the auto top-up settings doc (and therefore the light
  // threshold) is keyed on the real auth uid (pattern from TopUpModal).
  const [uid, setUid] = useState<string | null>(() => {
    const u = getAuth().currentUser;
    return u && !u.isAnonymous ? u.uid : null;
  });
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (u) => setUid(u && !u.isAnonymous ? u.uid : null));
    return unsub;
  }, []);

  const { settings: autoTopUpSettings } = useAutoTopUp(uid);
  const thresholdUsd = autoTopUpSettings.thresholdUsd; // defaults to $25

  const [dismissedAt, setDismissedAt] = useState<string | null>(() => readDailyDismissal(DISMISS_KEY));

  // Permission gate: billing chrome only for viewers who can act on or see
  // the wallet (owners are included in both).
  const permitted = vis.canManageBilling || vis.canSeeWallet;

  // ORG context where the viewer cannot act (2026-08-18 audit): the displayed
  // wallet is an organization's and the viewer lacks billing.manage — the
  // "Top up" CTA only led to the blocked modal (a dead end), and the critical
  // state was un-dismissable chrome the viewer could do nothing about. In
  // this mode: honest ask-an-admin copy, no dead-end button, and dismissible
  // for the session even when critical.
  // The rule lives in lowBalanceAudience.ts so the chat's own notice reads the
  // SAME answer — it used to have no ask-owner case at all and offered a
  // dead-end top-up button to exactly this reader (2026-09-13).
  const askOwnerMode = isAskOwnerAudience({
    displayScope: walletSummary?.displayScope,
    visibilityReady: vis.ready,
    canManageBilling: vis.canManageBilling,
  });
  const ASK_OWNER_DISMISS_KEY = 'prest0n_lowbal_askowner_dismissed';
  const [askOwnerDismissed, setAskOwnerDismissed] = useState<boolean>(() => {
    try { return sessionStorage.getItem(ASK_OWNER_DISMISS_KEY) === '1'; } catch { return false; }
  });

  // The balance is only trustworthy once the summary settled for a real user.
  const balanceSettled =
    !!walletSummary && walletSummary.hasUser && !walletSummary.isLoading && !lb.isLoading;

  const balanceUsd = lb.balanceUsd;

  // CRITICAL: at/below empty — the server balance stop has paused work. Not
  // dismissible.
  //
  // 2026-08-15 regression fix: this used to IGNORE the entitlement bypass, on
  // the belief that the platform rule pauses trial/comped accounts too. It
  // does not — the server now waives the balance stop for uncapped accounts
  // (functions/src/prest0nSession/accountSpendGate.ts), because they are
  // funded by an entitlement rather than by wallet money. A comped workspace
  // has no wallet balance by design, so ignoring the bypass pinned a
  // permanent, undismissable "you're out of credits, work is paused" banner on
  // accounts whose work was never paused. Mirror the server: no bypass, no
  // banner.
  // An account deliberately allowed to run past zero has no low-balance state
  // to report — neither the light nudge nor the un-dismissible critical strip
  // (2026-09-11). This is the same FACT the server keys on when it decides not
  // to send the alert (functions/src/notifications/lowBalanceDecision.ts, Miles
  // 2026-09-09: "we are allowed to go negative without getting cut off"), read
  // from the account's own spendLimits doc rather than from an org id, so the
  // two halves cannot drift and any account later granted the allowance goes
  // quiet on its own. Until then the client had been showing a permanently
  // un-dismissible "top up" strip to accounts the server had already decided
  // not to nag. Defaults to armed whenever the fact is unknown.
  const balanceStopArmed = lb.balanceStopArmed !== false;

  const criticalActive =
    permitted && !!uid && balanceSettled && balanceStopArmed &&
    !lb.hasEntitlementBypass && balanceUsd <= 0;

  // LIGHT: above empty but at/under the user's auto top-up threshold.
  // Trial/comped accounts with an entitlement bypass are not nagged.
  const lightActive =
    permitted &&
    !!uid &&
    balanceSettled &&
    balanceStopArmed &&
    !lb.hasEntitlementBypass &&
    balanceUsd > 0 &&
    balanceUsd <= thresholdUsd;

  // Emit the avatar bridge event ONCE per false→true transition of either state.
  const prevActiveRef = useRef(false);
  useEffect(() => {
    const active = (criticalActive || lightActive) && onAppSurface;
    if (active && !prevActiveRef.current) {
      broadcastBridgeEvent({
        event: 'wallet.balance.low',
        // balanceUsd stays — the event contract is unchanged; balanceWu is
        // ADDITIVE only (2026-08 PRO Work Units display migration).
        payload: {
          balanceUsd,
          balanceWu: usdToWu(balanceUsd),
          isCritical: criticalActive,
        },
      });
    }
    prevActiveRef.current = active;
  }, [criticalActive, lightActive, balanceUsd, onAppSurface]);

  // Light-state dismissal holds until the next 5 AM local (daily reset).
  const lightDismissed = isDismissalActive(dismissedAt);

  // Signed-in viewers only, and never mid-auth-flow (bannerSurface.ts).
  //
  // The old `&& !chatOpen` de-dup is GONE (2026-08-31 consistency directive).
  // It made the band's content depend on whether the chat happened to be
  // open, which is precisely the inconsistency the directive names: the same
  // account on the same page showed a different set of notices depending on
  // a panel's state, and in fullscreen chat the band lost its only critical
  // notice entirely. The chat's own low-balance notice is a message in the
  // transcript, not a top strip, so the two no longer occupy one slot.
  /**
   * ONE PLACE PER STATE, ON A PHONE (2026-09-13).
   *
   * The chat's own notice sits directly above the composer and says the same
   * thing this band says. On a desktop that is two strips on a large screen; on
   * a phone with the chat open it is the same sentence twice on a screen the
   * whole mobile programme has been reclaiming — and this band PINS itself
   * above the takeover, so both are on screen at once.
   *
   * The in-chat notice is the one that survives, for two reasons: it is
   * anchored to the composer the message is about ("that's why sending is
   * unavailable" only makes sense next to the thing that will not send), and
   * this band ROTATES, so at any given moment it may be showing something else
   * entirely. Since the same day it also carries the ask-owner variant, so
   * standing down here loses no reader anything.
   *
   * Narrow only, and only while the chat is actually the full screen. Desktop
   * is unchanged — the owner's standing instruction on this programme is not to
   * change much about it, and there the duplication costs clutter, not room.
   */
  const chatOwnsThePhoneScreen = chatFullScreen && narrowViewport;

  const show =
    onAppSurface &&
    !chatOwnsThePhoneScreen &&
    (askOwnerMode
      ? (criticalActive || lightActive) && !askOwnerDismissed
      : criticalActive || (lightActive && !lightDismissed));

  const handleTopUp = () => {
    if (topUp) {
      topUp.open('global-low-balance-banner');
    } else {
      // Fallback deep-link if the modal provider is unavailable.
      navigate('/wallet?action=add-tokens');
    }
  };

  const handleDismiss = () => {
    if (askOwnerMode) {
      try { sessionStorage.setItem(ASK_OWNER_DISMISS_KEY, '1'); } catch { /* session-only nicety */ }
      setAskOwnerDismissed(true);
      return;
    }
    writeDailyDismissal(DISMISS_KEY);
    setDismissedAt(readDailyDismissal(DISMISS_KEY));
  };

  const message = askOwnerMode
    ? criticalActive
      ? `this workspace is out of ${WU_FULL_NAME} — work is paused. ask an owner or billing admin to top up.`
      : `this workspace's balance is running low — ${formatWu(usdToWu(balanceUsd))} ${WU_LABEL} left. ask an owner or billing admin to top up.`
    : criticalActive
      ? `out of ${WU_FULL_NAME} — work is paused. top up or redeem a code to resume instantly.`
      : `your balance is running low — ${formatWu(usdToWu(balanceUsd))} ${WU_LABEL} left. top up to stay ahead of the pause.`;

  /**
   * The phone line (2026-09-13). The amount and the state are the facts a
   * reader acts on; "top up or redeem a code to resume instantly" and "to stay
   * ahead of the pause" are the elaboration, one tap away. The ask-owner
   * variants keep their instruction, because there the instruction IS the
   * message — this reader cannot top up and needs to know who can.
   */
  const shortMessage = askOwnerMode
    ? criticalActive
      ? `out of ${WU_FULL_NAME} — ask an owner to top up.`
      : `low balance — ${formatWu(usdToWu(balanceUsd))} ${WU_LABEL} left. ask an owner.`
    : criticalActive
      ? `out of ${WU_FULL_NAME} — work is paused.`
      : `low balance — ${formatWu(usdToWu(balanceUsd))} ${WU_LABEL} left.`;

  const renderContent = (body: React.ReactNode) => (
    <>
      <Message>{body}</Message>
      {!askOwnerMode && (
        <TopUpButton type="button" $critical={criticalActive} onClick={handleTopUp}>
          Top up
        </TopUpButton>
      )}
    </>
  );
  const content = renderContent(message);
  const shortContent = renderContent(shortMessage);

  // Register with the global banner center (dismissing there runs the same
  // daily-5AM dismiss policy; the state change then unregisters the banner).
  useRegisterBanner(
    show
      ? {
          id: 'low-balance',
          tone: criticalActive ? 'critical' : 'info',
          content,
          shortContent,
          dismissible: askOwnerMode || !criticalActive,
          onDismiss: askOwnerMode || !criticalActive ? handleDismiss : undefined,
        }
      : null,
  );

  // Inside a GlobalBannerCenter the center owns the strip.
  if (bus) return null;

  // ── Legacy standalone fallback (no center above) ──────────────────────────
  if (!show) return null;

  return (
    <Strip $critical={criticalActive} role="status" aria-live="polite">
      {content}
      {(askOwnerMode || !criticalActive) && (
        <DismissButton type="button" onClick={handleDismiss} aria-label="Dismiss">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </DismissButton>
      )}
    </Strip>
  );
};

export default GlobalLowBalanceBanner;
